// [DASHBOARD-STRIPE] - Dashboard niveau Stripe/Shopify (Refactored)
import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Wallet, ShoppingBag, ShoppingCart, Users, Loader2,
  RefreshCw, Store, ExternalLink, Clock, Sun, Moon, Package
} from "lucide-react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { PremiumDatePicker } from "../components/PremiumDatePicker";

// Sub-components
import { COLORS_DARK, COLORS_LIGHT, TOOLTIP_TEXTS, generateColorPalette, DashboardModal } from "../components/dashboard/DashboardShared";
import { KPICard } from "../components/dashboard/KPICard";
import { MainChart, TopProductsAndCategories } from "../components/dashboard/DashboardCharts";
import { DashboardRecentSales } from "../components/dashboard/DashboardRecentSales";
import { DashboardOrders } from "../components/dashboard/DashboardOrders";
import { DashboardAlerts } from "../components/dashboard/DashboardAlerts";
import { DashboardQuickStats } from "../components/dashboard/DashboardQuickStats";
// [LIVE-VENTES] Imports
import LiveSalesBlock from "../components/dashboard/LiveSalesBlock";
import VendeurDetailModal from "../components/dashboard/VendeurDetailModal";
import { getFeatures } from "../lib/boutiqueType";

export default function Dashboard() {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();

  // Modal states
  const [boostModal, setBoostModal] = useState(false);
  const [stockModal, setStockModal] = useState(false);
  // [LIVE-VENTES] Modal vendeur détail
  const [selectedVendeur, setSelectedVendeur] = useState(null);

  // États
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("7days");
  const [appliedRange, setAppliedRange] = useState({ start: new Date(), end: new Date() });
  const [chartMode, setChartMode] = useState("revenue");

  // Theme from Layout context
  const { isDark, toggleTheme } = useOutletContext();
  const COLORS = isDark ? COLORS_DARK : COLORS_LIGHT;
  // [LIVE-VENTES] Role check
  const userRole = localStorage.getItem("userRole") || "owner";

  // Données
  const [salesData, setSalesData] = useState(null);
  const [previousSalesData, setPreviousSalesData] = useState(null);
  const [customerStats, setCustomerStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  // Données boutique depuis localStorage
  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || boutiqueData.devise || "€";
  const primaryColor = boutiqueData.customColor || boutiqueData.colorPalette?.[0] || "#D4A853";
  const userName = user?.name?.split(' ')[0] || boutiqueData.adminName?.split(' ')[0] || 'Admin';
  // [PROFIL-CAISSE] Feature flags
  const features = useMemo(() => getFeatures(), []);

  // Date du jour en français
  const todayFormatted = useMemo(() => format(new Date(), "EEEE d MMMM yyyy", { locale: fr }), []);

  // Heure locale en temps réel
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const timeFormatted = useMemo(() => format(currentTime, "HH:mm:ss", { locale: fr }), [currentTime]);

  // URL du site vitrine
  const storefrontUrl = useMemo(() => (features.vitrine && user?.id) ? `/vitrine/${user.id}` : null, [user, features]);

  // Période API mapping
  const getPeriodParam = () => {
    switch (selectedPeriod) {
      case "today": case "yesterday": return "day";
      case "7days": return "week";
      case "30days": case "month": case "lastMonth": return "month";
      case "year": return "year";
      default: return "week";
    }
  };

  const handlePeriodApply = (presetId, range) => {
    setSelectedPeriod(presetId);
    setAppliedRange(range);
  };

  // Chargement des données
  const fetchData = useCallback(async (showRefreshAnimation = false) => {
    if (showRefreshAnimation) setRefreshing(true);
    else setLoading(true);
    try {
      const periodParam = getPeriodParam();
      const [salesRes, customersRes, alertsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/reports/sales-by-period?period=${periodParam}`, getAuthHeaders()),
        features.customers ? axios.get(`${API}/admin/customers/stats`, getAuthHeaders()).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        axios.get(`${API}/dashboard/alerts`, getAuthHeaders()).catch(() => ({ data: [] })),
        features.orders ? axios.get(`${API}/orders`, getAuthHeaders()).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setSalesData(salesRes.data);
      setCustomerStats(customersRes.data);
      setAlerts(alertsRes.data || []);
      setRecentInvoices(salesRes.data?.recent_invoices || []);
      const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      const sorted = [...orders].sort((a, b) => {
        const aScore = (a.unread_messages || 0) * 1000 + (a.status === 'new' ? 100 : 0);
        const bScore = (b.unread_messages || 0) * 1000 + (b.status === 'new' ? 100 : 0);
        if (bScore !== aScore) return bScore - aScore;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setRecentOrders(sorted.slice(0, 5));
      setPreviousSalesData({ total_sales: 0, transaction_count: 0, average_basket: 0, active_customers: 0 });
      if (showRefreshAnimation) toast.success("Données actualisées");
    } catch {
      toast.error("Erreur lors du chargement des données");
    } finally { setLoading(false); setRefreshing(false); }
  }, [selectedPeriod, getAuthHeaders]);

  const handleRefresh = () => fetchData(true);

  useEffect(() => { fetchData(); }, [selectedPeriod]);

  // Calcul des tendances
  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const generateSparklineData = (baseValue, variance = 0.2, points = 7) => {
    return Array.from({ length: points }, (_, i) => ({ day: i, value: baseValue * (1 + (Math.random() - 0.5) * variance) }));
  };

  // Chart data
  const chartData = useMemo(() => {
    const days = (selectedPeriod === "today" || selectedPeriod === "yesterday") ? 24 :
                 selectedPeriod === "7days" ? 7 :
                 (selectedPeriod === "month" || selectedPeriod === "30days" || selectedPeriod === "lastMonth") ? 30 :
                 selectedPeriod === "year" ? 12 : 7;
    const baseRevenue = (salesData?.total_sales || 0) / days;
    const baseTransactions = (salesData?.transaction_count || 0) / days;
    const now = new Date();
    return Array.from({ length: days }, (_, i) => {
      const date = new Date(now);
      if (selectedPeriod === "today" || selectedPeriod === "yesterday") {
        date.setHours(date.getHours() - (days - 1 - i));
        return { label: format(date, "HH:mm"), fullDate: format(date, "EEEE d MMMM, HH:mm", { locale: fr }),
          revenue: Math.round(baseRevenue * (0.5 + Math.random())), transactions: Math.round(baseTransactions * (0.5 + Math.random()) + 0.5) };
      } else if (selectedPeriod === "year") {
        date.setMonth(date.getMonth() - (days - 1 - i));
        return { label: format(date, "MMM", { locale: fr }), fullDate: format(date, "MMMM yyyy", { locale: fr }),
          revenue: Math.round(baseRevenue * (0.6 + Math.random() * 0.8)), transactions: Math.round(baseTransactions * (0.6 + Math.random() * 0.8) + 0.5) };
      } else {
        date.setDate(date.getDate() - (days - 1 - i));
        return { label: format(date, "dd/MM"), fullDate: format(date, "EEEE d MMMM", { locale: fr }),
          revenue: Math.round(baseRevenue * (0.3 + Math.random() * 1.4)), transactions: Math.round(baseTransactions * (0.3 + Math.random() * 1.4) + 0.5) };
      }
    });
  }, [salesData, selectedPeriod]);

  // KPI data
  const kpiData = useMemo(() => {
    if (!salesData) return [];
    const revenueTrend = calculateTrend(salesData.total_sales, previousSalesData?.total_sales);
    const salesTrend = calculateTrend(salesData.transaction_count, previousSalesData?.transaction_count);
    const basketTrend = calculateTrend(salesData.average_basket, previousSalesData?.average_basket);
    const customersTrend = calculateTrend(customerStats?.total_customers || 0, previousSalesData?.active_customers);
    return [
      { title: "Chiffre d'affaires", value: salesData.total_sales || 0,
        formattedValue: `${(salesData.total_sales || 0).toLocaleString('fr-FR')} ${devise}`,
        trend: revenueTrend, trendLabel: "vs période préc.", icon: Wallet,
        sparklineData: generateSparklineData(salesData.total_sales || 1000, 0.3), chartType: "area", tooltipText: TOOLTIP_TEXTS.revenue },
      { title: "Ventes", value: salesData.transaction_count || 0,
        formattedValue: `${salesData.transaction_count || 0} transactions`,
        trend: salesTrend, trendLabel: "vs période préc.", icon: ShoppingBag,
        sparklineData: generateSparklineData(salesData.transaction_count || 10, 0.4), chartType: "bar", tooltipText: TOOLTIP_TEXTS.sales },
      { title: "Panier moyen", value: salesData.average_basket || 0,
        formattedValue: `${(salesData.average_basket || 0).toLocaleString('fr-FR')} ${devise}`,
        trend: basketTrend, trendLabel: "vs période préc.", icon: ShoppingCart,
        sparklineData: generateSparklineData(salesData.average_basket || 100, 0.2), chartType: "line", tooltipText: TOOLTIP_TEXTS.avgBasket },
      features.customers
        ? { title: "Clientes actives", value: customerStats?.total_customers || 0,
            formattedValue: `${customerStats?.total_customers || 0} clientes`,
            trend: customersTrend, trendLabel: customerStats?.new_this_month ? `+${customerStats.new_this_month} ce mois` : "vs période préc.",
            icon: Users, sparklineData: generateSparklineData(customerStats?.total_customers || 5, 0.25), chartType: "line", tooltipText: TOOLTIP_TEXTS.customers }
        : { title: "Articles vendus", value: salesData.items_sold || 0,
            formattedValue: `${salesData.items_sold || 0} articles`,
            trend: salesTrend, trendLabel: "vs période préc.", icon: Package,
            sparklineData: generateSparklineData(salesData.items_sold || 10, 0.3), chartType: "bar", tooltipText: "Nombre total d'articles vendus sur la période sélectionnée." }
    ];
  }, [salesData, previousSalesData, customerStats, devise, features]);

  const colorPalette = useMemo(() => generateColorPalette(primaryColor, 6), [primaryColor]);

  // Top Produits data
  const [topProductsData, setTopProductsData] = useState([]);
  useEffect(() => {
    const fetchTopProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`, getAuthHeaders());
        const products = Array.isArray(res.data) ? res.data : [];
        const sorted = products
          .map(p => {
            const totalQty = (p.variants || []).reduce((sum, v) => sum + (v.quantity || 0), 0);
            return { id: p.id, name: p.name, quantity: totalQty, revenue: Math.round(p.sale_price * totalQty) };
          })
          .sort((a, b) => b.revenue - a.revenue).slice(0, 5);
        setTopProductsData(sorted);
      } catch { setTopProductsData([]); }
    };
    fetchTopProducts();
  }, [getAuthHeaders]);

  // Catégories data pour Pie Chart
  const [categoryData, setCategoryData] = useState([]);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/fashion-stats`, getAuthHeaders());
        const topCats = res.data?.top_categories || [];
        const total = topCats.reduce((s, c) => s + c.count, 0) || 1;
        setCategoryData(topCats.map((cat, i) => ({
          name: cat.name, value: cat.count,
          percent: Math.round((cat.count / total) * 100),
          fill: colorPalette[i % colorPalette.length]
        })));
      } catch { setCategoryData([]); }
    };
    fetchCategories();
  }, [getAuthHeaders, colorPalette]);

  // Alertes intelligentes
  const smartAlerts = useMemo(() => {
    const alertsList = [];
    if (alerts && alerts.length > 0) {
      alerts.filter(a => a.quantity === 0).forEach(product => {
        alertsList.push({ id: `oos-${product.id}`, type: "critical", title: `${product.name} en rupture de stock`,
          subtitle: "Réapprovisionnement urgent", action: () => navigate(`/products?search=${encodeURIComponent(product.name)}`) });
      });
      alerts.filter(a => a.quantity > 0 && a.quantity <= 3).forEach(product => {
        alertsList.push({ id: `low-${product.id}`, type: "warning", title: `${product.name} — stock faible`,
          subtitle: `${product.quantity} unité${product.quantity > 1 ? 's' : ''} restante${product.quantity > 1 ? 's' : ''}`,
          action: () => navigate(`/products?search=${encodeURIComponent(product.name)}`) });
      });
    }
    if (!salesData?.transaction_count || salesData.transaction_count === 0) {
      alertsList.push({ id: "no-sales", type: "critical", title: "Aucune vente sur cette période",
        subtitle: "Lancez une promotion pour booster vos ventes", action: () => setBoostModal(true) });
    }
    const revenueTrend = previousSalesData?.total_sales ? ((salesData?.total_sales - previousSalesData.total_sales) / previousSalesData.total_sales) * 100 : 0;
    if (revenueTrend < -15) {
      alertsList.push({ id: "revenue-down", type: "warning", title: `CA en baisse de ${Math.abs(Math.round(revenueTrend))}%`,
        subtitle: "vs période précédente", action: () => document.querySelector('[data-testid="chart-mode-revenue"]')?.scrollIntoView({ behavior: 'smooth' }) });
    }
    if (salesData?.total_sales > 0 && revenueTrend > 30) {
      alertsList.push({ id: "record-ca", type: "success", title: "Record battu ! Meilleur CA du mois",
        subtitle: `+${Math.round(revenueTrend)}% vs période précédente`, action: () => confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } }) });
    }
    if (salesData?.transaction_count === 1) {
      alertsList.push({ id: "first-sale", type: "success", title: "Première vente réalisée !",
        subtitle: "Continuez sur cette lancée", action: () => confetti({ particleCount: 150, spread: 100, origin: { y: 0.6 }, colors: ['#D4A853', '#FFD700', '#FFA500'] }) });
    }
    if (customerStats?.new_this_month && customerStats.new_this_month >= 5) {
      alertsList.push({ id: "new-customers", type: "success", title: `${customerStats.new_this_month} nouvelles clientes ce mois !`,
        subtitle: "Votre boutique attire de plus en plus", action: () => navigate("/customers") });
    }
    return alertsList;
  }, [salesData, previousSalesData, alerts, customerStats, navigate]);

  // Stats rapides
  const peakActivityHour = useMemo(() => salesData?.transaction_count > 0 ? "14h — 16h" : null, [salesData]);
  const bestCustomer = useMemo(() => customerStats?.top_customer || null, [customerStats]);
  const [stockValue, setStockValue] = useState({ value: 0, itemCount: 0 });
  useEffect(() => {
    const fetchStock = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/stats`, getAuthHeaders());
        setStockValue({ value: Math.round(res.data.total_sale_value || 0), itemCount: res.data.total_products || 0 });
      } catch { setStockValue({ value: 0, itemCount: 0 }); }
    };
    fetchStock();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 overflow-x-hidden" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col gap-3 md:gap-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 md:gap-3">
              <h1 className="text-lg md:text-2xl font-bold">Bonjour {userName}</h1>
              <button onClick={toggleTheme} className="theme-toggle-btn" style={{ position: 'static', boxShadow: 'none' }}
                data-testid="theme-toggle-dashboard" title={isDark ? "Mode clair" : "Mode sombre"}>
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-center gap-2 md:gap-3 mt-1 flex-wrap">
              <p className="text-xs md:text-sm text-muted-foreground capitalize">{todayFormatted}</p>
              <span className="text-muted-foreground/50 hidden md:inline">•</span>
              <div className="hidden md:flex items-center gap-1.5 text-sm font-medium" style={{ color: primaryColor }}>
                <Clock className="w-3.5 h-3.5" />
                <span data-testid="current-time">{timeFormatted}</span>
              </div>
              {storefrontUrl && (
                <>
                  <span className="text-muted-foreground/50 hidden md:inline">•</span>
                  <Link to={storefrontUrl} target="_blank"
                    className="hidden md:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
                    data-testid="storefront-link">
                    <Store className="w-3.5 h-3.5" /><span>Voir ma boutique</span>
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </>
              )}
            </div>
          </div>
          <button onClick={handleRefresh} className="md:hidden w-9 h-9 rounded-lg flex items-center justify-center border"
            style={{ borderColor: isDark ? '#2A2A2A' : '#E5E7EB' }} data-testid="mobile-refresh-btn">
            <RefreshCw className={`w-4 h-4 text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <PremiumDatePicker isDark={isDark} activePreset={selectedPeriod} appliedRange={appliedRange} onApply={handlePeriodApply} />
      </div>

      {/* [LIVE-VENTES] Bloc activité en direct — owner uniquement */}
      {userRole === "owner" && (
        <LiveSalesBlock
          onNewSaleCount={(count) => {
            // Badge handled via Layout
          }}
          onVendeurClick={(v) => setSelectedVendeur(v)}
        />
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {kpiData.map((kpi, index) => (
          <KPICard key={kpi.title} {...kpi} delay={index * 100} primaryColor={primaryColor} isDark={isDark} />
        ))}
      </div>

      {/* Orders Widget — vitrine/commandes uniquement (profil mode) */}
      {features.orders && (
        <DashboardOrders recentOrders={recentOrders} primaryColor={primaryColor} devise={devise} />
      )}

      {/* Main Chart */}
      <MainChart chartData={chartData} chartMode={chartMode} setChartMode={setChartMode}
        primaryColor={primaryColor} devise={devise} isDark={isDark} selectedPeriod={selectedPeriod} COLORS={COLORS} />

      {/* Top Products & Categories */}
      <TopProductsAndCategories topProductsData={topProductsData} categoryData={categoryData}
        salesData={salesData} primaryColor={primaryColor} devise={devise} isDark={isDark} colorPalette={colorPalette} COLORS={COLORS} />

      {/* Recent Sales + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <DashboardRecentSales recentInvoices={recentInvoices} primaryColor={primaryColor} devise={devise} isDark={isDark} />
        <DashboardAlerts alerts={alerts} COLORS={COLORS} />
      </div>

      {/* Quick Stats */}
      <DashboardQuickStats peakActivityHour={peakActivityHour} bestCustomer={bestCustomer}
        stockValue={stockValue} primaryColor={primaryColor} devise={devise} onStockClick={() => setStockModal(true)} />

      {/* Boost Sales Modal */}
      <DashboardModal isOpen={boostModal} onClose={() => setBoostModal(false)} title="Boostez vos ventes">
        <p style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', marginBottom: 16, lineHeight: 1.5 }}>
          Vous n'avez aucune vente sur cette période. Voici quelques actions rapides :
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button data-testid="boost-promotion-btn" onClick={() => { setBoostModal(false); navigate('/promotions'); }}
            style={{ padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, background: 'hsl(var(--secondary))',
              color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))', cursor: 'pointer', textAlign: 'left' }}>
            Lancer une promotion
          </button>
          <button data-testid="boost-share-btn" onClick={() => {
              const link = `${window.location.origin}/boutique/${boutiqueData?.slug || ''}`;
              navigator.clipboard.writeText(link); setBoostModal(false); }}
            style={{ padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, background: 'hsl(var(--secondary))',
              color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))', cursor: 'pointer', textAlign: 'left' }}>
            Partager ma boutique
          </button>
          <button data-testid="boost-caisse-btn" onClick={() => { setBoostModal(false); navigate('/admin/sales'); }}
            style={{ padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, background: '#FF4500',
              color: '#FFFFFF', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            Aller à la caisse
          </button>
        </div>
      </DashboardModal>

      {/* Stock Modal */}
      <DashboardModal isOpen={stockModal} onClose={() => setStockModal(false)} title="Stock actuel">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px',
            borderRadius: 10, background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))' }}>
            <span style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))' }}>Valeur totale</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: primaryColor }}>{stockValue.value.toLocaleString('fr-FR')} {devise}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px',
            borderRadius: 10, background: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))' }}>
            <span style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))' }}>Articles en stock</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'hsl(var(--foreground))' }}>{stockValue.itemCount}</span>
          </div>
        </div>
        <button data-testid="stock-manage-btn" onClick={() => { setStockModal(false); navigate('/products'); }}
          style={{ width: '100%', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
            background: '#FF4500', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}>
          Gérer les articles →
        </button>
      </DashboardModal>

      {/* [LIVE-VENTES] Modal détail vendeur */}
      <VendeurDetailModal
        vendeur={selectedVendeur}
        open={!!selectedVendeur}
        onClose={() => setSelectedVendeur(null)}
      />
    </div>
  );
}
